import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { useAuth } from '~/auth/auth-context'
import { useDataService, useDataState } from '~/data/data-context'
import { displayToKg, kgToDisplay } from '~/lib/units'

export const Route = createFileRoute('/sets_/$setId/edit')({
  component: EditSet,
})

/**
 * Correcting a set that is already saved. It is reached from the set itself and nowhere else —
 * sets are logged in a workout, and this is only the way to fix one after the fact. The
 * exercise is not up for changing here: a set of the wrong exercise is a different set.
 */
function EditSet() {
  const { setId } = Route.useParams()
  const state = useDataState()
  const service = useDataService()
  const navigate = useNavigate()
  const { units } = useAuth()

  const set = state.sets.find((it) => it.id === setId) ?? null
  const records = set?.records ?? null

  const [weight, setWeight] = useState(() =>
    set?.weightKg === null || set?.weightKg === undefined
      ? ''
      : String(kgToDisplay(set.weightKg, units)),
  )
  const [reps, setReps] = useState(() => (set?.reps == null ? '' : String(set.reps)))
  const [duration, setDuration] = useState(() =>
    set?.durationSeconds == null ? '' : String(set.durationSeconds),
  )
  const [distance, setDistance] = useState(() =>
    set?.distanceMeters == null ? '' : String(set.distanceMeters),
  )
  const [warmup, setWarmup] = useState(() => set?.isWarmup ?? false)

  if (set === null || records === null) {
    return <p className="p-6 text-ink-muted">This set is no longer here.</p>
  }

  async function save(): Promise<void> {
    if (set === null || records === null) {
      return
    }
    await service.editSet(set.id, {
      exerciseName: set.exerciseName,
      performedAt: set.performedAt,
      ...(records.weight && weight !== '' ? { weightKg: displayToKg(Number(weight), units) } : {}),
      ...(records.reps && reps !== '' ? { reps: Number(reps) } : {}),
      ...(records.duration && duration !== '' ? { durationSeconds: Number(duration) } : {}),
      ...(records.distance && distance !== '' ? { distanceMeters: Number(distance) } : {}),
      isWarmup: warmup,
      notes: set.notes,
      rpe: set.rpe,
    })
    void navigate({ to: '/sets/$setId', params: { setId: set.id } })
  }

  return (
    <section className="mx-auto w-full max-w-md space-y-5 px-4 py-6">
      <h1 className="text-2xl font-medium">Edit set</h1>
      <p className="text-ink-muted">{set.exerciseName}</p>

      {records.weight && (
        <Field label={`Weight (${units})`} value={weight} step={0.5} onChange={setWeight} />
      )}
      {records.reps && <Field label="Reps" value={reps} step={1} onChange={setReps} />}
      {records.duration && (
        <Field
          label={records.distance ? 'Time (seconds)' : 'Duration (seconds)'}
          value={duration}
          step={1}
          onChange={setDuration}
        />
      )}
      {records.distance && (
        <Field label="Distance (metres)" value={distance} step={5} onChange={setDistance} />
      )}

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={warmup}
          onChange={(event) => {
            setWarmup(event.target.checked)
          }}
        />
        Warm-up
      </label>

      <button
        type="button"
        className="w-full rounded-full bg-accent px-5 py-2 font-medium text-on-accent"
        onClick={() => void save()}
      >
        Save set
      </button>
    </section>
  )
}

function Field({
  label,
  value,
  step,
  onChange,
}: {
  label: string
  value: string
  step: number
  onChange: (value: string) => void
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm text-ink-muted">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step={step}
        aria-label={label}
        className="w-full rounded-2xl bg-surface-raised p-3"
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      />
    </label>
  )
}
